import { Link } from "@inertiajs/react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Input } from "@/components/ui/input"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import type { PickerOption } from "@/types/messaging"

/**
 * OKS-37 — "Pick a creator to start a thread" sheet.
 *
 * Triggered from the conversation-list empty state. Lists active
 * crew members (server-pre-filtered to brand + visible status).
 * Selecting an option navigates to `/crew/messages/:id` — if the
 * thread doesn't exist yet, the brand operator's first send creates
 * it lazily.
 *
 * Picker options are an Inertia DEFERRED prop — `undefined` until
 * the parent page calls `router.reload({ only: ['crewMembersForPicker'] })`
 * (which it does on `onOpenChange(true)`). Until they arrive we
 * render a skeleton.
 */
interface CreatorPickerSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: PickerOption[] | undefined
}

export function CreatorPickerSheet({
  open,
  onOpenChange,
  options,
}: CreatorPickerSheetProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")

  const filtered = useMemo(() => {
    if (options == null) return null
    const q = query.trim().toLowerCase()
    if (q === "") return options
    return options.filter(
      (o) =>
        o.displayName.toLowerCase().includes(q) ||
        (o.tiktokHandle?.toLowerCase() ?? "").includes(q),
    )
  }, [options, query])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-md flex-col"
        data-testid="messaging-picker-sheet"
      >
        <SheetHeader>
          <SheetTitle>{t("messaging.brand.picker.sheet_title")}</SheetTitle>
          <SheetDescription>
            {t("messaging.brand.picker.sheet_description")}
          </SheetDescription>
        </SheetHeader>
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("messaging.brand.list.search_placeholder")}
          className="mt-3"
          data-testid="messaging-picker-search"
        />
        <ul role="list" className="mt-3 flex-1 overflow-y-auto">
          {filtered == null ? (
            <div className="space-y-2" data-testid="messaging-picker-loading">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p
              className="text-muted-foreground p-3 text-sm"
              data-testid="messaging-picker-no-results"
            >
              {t("messaging.brand.picker.no_results")}
            </p>
          ) : (
            filtered.map((opt) => (
              <li key={opt.crewMembershipId} role="listitem">
                <Link
                  href={opt.url}
                  onClick={() => onOpenChange(false)}
                  className="hover:bg-accent flex items-center justify-between rounded p-2 text-sm"
                  data-testid={`messaging-picker-row-${opt.crewMembershipId}`}
                >
                  <span className="truncate">
                    <span className="font-medium">{opt.displayName}</span>
                    {opt.tiktokHandle && (
                      <span className="text-muted-foreground ml-2 text-xs">
                        @{opt.tiktokHandle}
                      </span>
                    )}
                  </span>
                  {opt.hasExistingThread && (
                    <span className="bg-muted text-muted-foreground ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px]">
                      {t("messaging.brand.picker.open_thread_badge")}
                    </span>
                  )}
                </Link>
              </li>
            ))
          )}
        </ul>
      </SheetContent>
    </Sheet>
  )
}
